import { Injectable } from '@angular/core';
import { AppConfig } from '../../app-config';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FeatureFlag } from './i-feature-flag';
import { Features } from './features';
import { ContentType } from 'src/app/content-management/shared/enums/content-type';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import { HttpClient } from '@angular/common/http';
import {
  CitywireInternalAccountsService,
  ICitywireUser,
} from '@tpapa/citywire-internal-accounts-angular';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagsService {
  private readonly FEATURES_ROUTE = `/api/features`;
  features: FeatureFlag[] = [];

  constructor(
    private appConfig: AppConfig,
    private accountsService: CitywireInternalAccountsService,
    private httpClient: HttpClient
  ) {}

  public async load(): Promise<void> {
    this.features = await this.httpClient
      .get<FeatureFlag[]>(this.appConfig.cmsApiBaseUrl + this.FEATURES_ROUTE)
      .toPromise();
  }

  isFeatureEnabledForUser$(feature: Features): Observable<boolean> {
    return this.evaluate$(feature, (user) => user.username);
  }

  // Same gate as isFeatureEnabledForUser$ but the allowlist is matched against the user's email
  // rather than their username - used by flags managed as an email allowlist (e.g. smart tagging).
  isFeatureEnabledForUserByEmail$(feature: Features): Observable<boolean> {
    return this.evaluate$(feature, (user) => user.email);
  }

  private evaluate$(
    feature: Features,
    identity: (user: ICitywireUser) => string,
  ): Observable<boolean> {
    if (!feature) {
      return of(false);
    }
    const featureConfig = this.features.find(
      (p) => p.featureName === feature.toString() && p.isActive
    );

    if (!featureConfig) {
      return of(false); //no flag was found in settings or it's disabled we default to feature disabled
    }

    if (!featureConfig.isABTesting) {
      return of(true); //AB testing disabled, feature enabled for everyone
    }

    // NOTE: currentUser starts as null before sign-in, and nobody is allowlisted until there is a user.
    return this.accountsService.currentUser.pipe(
      take(1),
      map((user) =>
        !!user &&
        featureConfig.abUsernames.some(
          (p) => p === (identity(user) ?? '').toLowerCase()
        )
      )
    );
  }

  isEnabledForContentType$(contentType: ContentType): Observable<boolean> {
    if (typeof contentType === 'string') {
      var enumContentType: ContentType =
        ContentType[contentType as keyof typeof ContentType];
      contentType = enumContentType;
    }
    const feature = this.getFeatureForContentType(contentType);
    return this.isFeatureEnabledForUser$(feature);
  }

  getFeatureForContentType(contentType: ContentType) {
    switch (contentType) {
      case ContentType.Article:
        return Features.ARTICLE;
      case ContentType.Gallery:
        return Features.GALLERY;
      case ContentType.DigitalPublication:
        return Features.DIGITAL_PUBLICATION;
      case ContentType.Page:
        return Features.PAGE;
      case ContentType.SingleSponsor:
        return Features.SINGLE_SPONSOR;
      case ContentType.ContentSet:
        return Features.CONTENT_SET;
      default:
        break;
    }
  }

  getRouteForContentType(
    contentType: string,
    contentId: string,
    state: ContentStatus
  ) {
    if (
      state === ContentStatus.PUBLISHED ||
      state === ContentStatus.WAITING_FOR_EDITING ||
      state === ContentStatus.WAITING_FOR_JOURNALIST_EDITING
    ) {
      return [
        '/content',
        ContentTypeRouterPaths[
          <ContentTypes>contentType.toString().toLowerCase()
        ],
        contentId,
      ];
    }
    return [
      '/content',
      ContentTypeRouterPaths[
        <ContentTypes>contentType.toString().toLowerCase()
      ],
      contentId,
      'edit',
    ];
  }
}
